import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ShieldExclamationIcon } from '@/shared/helpers/Icons';
import classNames from 'classnames';
import Hooks from '@/shared/hooks';
import { Tooltip } from '@material-ui/core';
import axios from 'axios';
import { Spinner } from 'reactstrap';

// Global tracker to prevent duplicate polling for the same prospect
const pollingProspects = new Set();

const TrinityCheck = ({ deal, onUpdate }) => {
  const [showTrinityModal] = Hooks.useShowModalHook('trinity-info-modal');
  const { showError } = Hooks.useNoticeHook();
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const prospect = deal?.prospect;
  const isUpdated = useMemo(() => prospect?.approvalStatus?.ai?.updatedAt && prospect?.approvalStatus?.ai?.eventId, [prospect]);
  const isLoading = useMemo(() => {
    const loading = prospect?.approvalStatus?.ai?.loading || false;
    return loading;
  }, [prospect]);
  const isMounted = useRef(true);
  const status = useMemo(() => prospect?.approvalStatus?.ai?.status || null, [prospect]);
  const notes = useMemo(() => prospect?.approvalStatus?.ai?.notes || null, [prospect]);

  const checkLoadingStatus = async (force) => {
    if (!isLoading && !force) return;
    if (!isMounted.current) return;
    if (!prospect?._id) return;

    const prospectId = prospect._id.toString();

    // Prevent duplicate polling for the same prospect
    if (pollingProspects.has(prospectId)) {
      return;
    }

    pollingProspects.add(prospectId);

    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      const response = await axios.post(`/api/brand/deals/post-get-prospect-ai-status`, {
        prospectId: prospect._id,
      });
      if (response.data.success && response.data.aiStatus) {
        const aiStatus = response.data.aiStatus;

        // Update all deals with the same prospect ID with the new approval status
        if (onUpdate) {
          const updatedProspect = {
            ...prospect,
            approvalStatus: {
              ...prospect.approvalStatus,
              ai: aiStatus
            }
          };
          onUpdate(deal.dealId, {
            prospect: updatedProspect
          }, prospectId);
        }

        if (aiStatus.loading) {
          await new Promise(resolve => setTimeout(resolve, 10000));
          pollingProspects.delete(prospectId); // Remove before recursive call
          await checkLoadingStatus();
        } else {
          pollingProspects.delete(prospectId);
        }
      } else {
        pollingProspects.delete(prospectId);
      }
    } catch (error) {
      console.error('Error checking loading status:', error);
      pollingProspects.delete(prospectId);
    }
  };
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Clean up polling tracker on unmount
      if (prospect?._id) {
        pollingProspects.delete(prospect._id.toString());
      }
    };
  }, [prospect?._id]);
  const loadData = async () => {
    if (!prospect?._id) return;

    const prospectId = prospect._id.toString();

    // Prevent duplicate analysis requests
    if (pollingProspects.has(prospectId) || isLoadingRecord) {
      return;
    }

    const brandName = deal?.brand;
    try {
      setIsLoadingRecord(true);
      const res = await axios.post('/api/brand/deals/post-trinity-analyse-q', {
        prospectId: prospect._id,
        brand: brandName,
        useTranscript: 10,
      });
      if(res.data.success === true) {
        await checkLoadingStatus(true);
      } else {
        throw new Error(res.data.error || 'Error fetching Trinity result');
      }

      /* if (res.data.prospectPopulated) {
        updateRecordFunction(res.data.prospectPopulated);
      } */
    } catch (error) {
      showError(error.message || 'Error fetching Trinity result');
      console.error('Error fetching Trinity result:', error);
    } finally {
      setIsLoadingRecord(false);
    }
  };
  const handleSafetyCheck = async () => {
    if (isLoading || !prospect) return;
    try {
      if (isUpdated) {
        showTrinityModal({ prospect })
      } else {
        await loadData();
      }
      // await onCheck(id);

    } catch (error) {
      console.error('Safety check failed:', error);
    }
  };

  

  React.useEffect(() => {
    if (isLoading) {
      checkLoadingStatus();
    }
  }, [isLoading]);

  if (!prospect) {
    return null;
  }

  return (
    <Tooltip title={isUpdated ? notes : 'Run Safety Check'}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
      <a
        role="button"
        onClick={handleSafetyCheck}
        className={classNames(
          'ml-1 cursor-pointer',
          {
            'text-muted': !isUpdated,
            'text-success': status === 'Approved',
            'text-danger': status === 'Rejected',
          }
        )}
      >
        {isLoading || isLoadingRecord ?<Spinner style={{ fontSize: '12px', width: '20px', height: '20px' }} /> : <ShieldExclamationIcon
          className="h-5 w-5"
          aria-hidden="true"
        />}
      </a>
    </Tooltip>
  );
};

export default TrinityCheck;